import {z} from 'zod'

import {apiFetch, downloadApiBlob} from 'src/api/fetch'

import {
    type CreateWorkerInput,
    createWorkerRequestSchema,
    type ListWorkerAssignmentsOptions,
    type ListWorkersOptions,
    type ListWorkerTimeEntriesOptions,
    type UpdateWorkerInput,
    updateWorkerRequestSchema,
    type Worker,
    type WorkerAssignmentList,
    workerAssignmentListSchema,
    type WorkerDetail,
    workerDetailSchema,
    type WorkerImportPreview,
    workerImportPreviewSchema,
    type WorkerImportResult,
    workerImportResultSchema,
    workerSchema,
    type WorkerTimeEntryList,
    workerTimeEntryListSchema,
} from './schemas'

export async function listWorkers(options?: ListWorkersOptions): Promise<Worker[]> {
    const params = new URLSearchParams()
    if (options?.workerTypeId) {
        params.set('worker_type_id', options.workerTypeId)
    }
    if (options?.includeArchived) {
        params.set('include_archived', 'true')
    }
    const query = params.toString()
    const raw = await apiFetch<unknown>(`/workers${query ? `?${query}` : ''}`)
    return z.array(workerSchema).parse(raw)
}

export async function getWorker(id: string): Promise<WorkerDetail> {
    const raw = await apiFetch<unknown>(`/workers/${id}`)
    return workerDetailSchema.parse(raw)
}

export async function createWorker(input: CreateWorkerInput): Promise<Worker> {
    const body = createWorkerRequestSchema.parse(input)
    const raw = await apiFetch<unknown>('/workers', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return workerSchema.parse(raw)
}

export async function updateWorker(id: string, input: UpdateWorkerInput): Promise<WorkerDetail> {
    const body = updateWorkerRequestSchema.parse(input)
    const raw = await apiFetch<unknown>(`/workers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return workerDetailSchema.parse(raw)
}

export async function archiveWorker(id: string): Promise<WorkerDetail> {
    const raw = await apiFetch<unknown>(`/workers/${id}/archive`, {method: 'POST'})
    return workerDetailSchema.parse(raw)
}

export async function unarchiveWorker(id: string): Promise<WorkerDetail> {
    const raw = await apiFetch<unknown>(`/workers/${id}/unarchive`, {method: 'POST'})
    return workerDetailSchema.parse(raw)
}

export async function listWorkerAssignments(
    workerId: string,
    options: ListWorkerAssignmentsOptions,
): Promise<WorkerAssignmentList> {
    const query = new URLSearchParams({
        date_from: options.dateFrom,
        date_to: options.dateTo,
    })
    const raw = await apiFetch<unknown>(`/workers/${workerId}/assignments?${query}`)
    return workerAssignmentListSchema.parse(raw)
}

export async function listWorkerTimeEntries(
    workerId: string,
    options?: ListWorkerTimeEntriesOptions,
): Promise<WorkerTimeEntryList> {
    const query = options?.limit != null ? `?limit=${options.limit}` : ''
    const raw = await apiFetch<unknown>(`/workers/${workerId}/time-entries${query}`)
    return workerTimeEntryListSchema.parse(raw)
}

export async function downloadWorkerImportTemplate(): Promise<void> {
    await downloadApiBlob('/workers/import/template', 'workers-import-template.csv')
}

export async function previewWorkerImport(file: File): Promise<WorkerImportPreview> {
    const formData = new FormData()
    formData.append('file', file)

    const raw = await apiFetch<unknown>('/workers/import/preview', {
        method: 'POST',
        body: formData,
    })
    return workerImportPreviewSchema.parse(raw)
}

export async function importWorkersCsv(file: File): Promise<WorkerImportResult> {
    const formData = new FormData()
    formData.append('file', file)

    const raw = await apiFetch<unknown>('/workers/import', {
        method: 'POST',
        body: formData,
    })
    return workerImportResultSchema.parse(raw)
}
